import * as React from "react";
import cn from "classnames";
import { useOnClickOutside } from "@brite-inc/ui-hooks/dist/clickOutside";
import { useKeyboard } from "@brite-inc/ui-hooks/dist/keyboard";
import { Popover } from "../Popover";
import styles from "./styles.module.scss";

interface IProps {
    onClose: () => void;
    children?: React.ReactNode;
    targetRef?: React.RefObject<HTMLElement>;
    title?: string;
    excludeRefs?: Array<HTMLElement | null>;
    popoverClassName?: string;
    dropdownClassName?: string;
    dropdownContentClassName?: string;
    "data-testid"?: string;
}

export function Dropdown({
    onClose,
    children,
    title,
    excludeRefs,
    popoverClassName,
    dropdownClassName,
    dropdownContentClassName,
    "data-testid": dataTestId,
    targetRef,
}: IProps) {
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const dropdownContentRef = React.useRef<HTMLDivElement>(null);

    useOnClickOutside(
        dropdownRef,
        () => {
            onClose();
        },
        excludeRefs,
    );

    const handleKeydown = (event: React.KeyboardEvent) => {
        const ESC_KEY = 27;

        if (
            event.code === "Tab" &&
            !dropdownContentRef.current?.contains(event.currentTarget)
        ) {
            event.stopPropagation();
            onClose();
        }

        if (event.code === "Escape" || event.keyCode === ESC_KEY) {
            event.stopPropagation();
            onClose();
        }
    };

    const { keyboardProps } = useKeyboard({
        onKeyDown: handleKeydown,
    });

    const disableScroll = () => {
        document
            .querySelector(".contentWithSidebar")
            ?.classList.add(styles.disableScroll);

        document
            .querySelector(".ReactModal__Content")
            ?.classList.add(styles.disableScroll);
    };

    const enableScroll = () => {
        document
            .querySelector(".contentWithSidebar")
            ?.classList.remove(styles.disableScroll);

        document
            .querySelector(".ReactModal__Content")
            ?.classList.remove(styles.disableScroll);
    };

    const focusDropdownContent = () => {
        dropdownContentRef.current?.focus();
    };

    React.useEffect(() => {
        focusDropdownContent();
        disableScroll();

        return () => {
            enableScroll();
        };
    }, []);

    return (
        <Popover targetRef={targetRef} className={popoverClassName}>
            <div
                className={cn([
                    styles.dropdown,
                    dropdownClassName && dropdownClassName,
                ])}
                ref={dropdownRef}
                data-testid={dataTestId}
            >
                <div
                    className={cn([
                        styles.dropdownContent,
                        dropdownContentClassName && dropdownContentClassName,
                    ])}
                    tabIndex={-1}
                    ref={dropdownContentRef}
                    {...keyboardProps}
                >
                    {title ? (
                        <h4 className={styles.dropdownTitle}>{title}</h4>
                    ) : null}

                    <div className={styles.dropdownBody}>{children}</div>
                </div>
            </div>
        </Popover>
    );
}
